<template>
  <div class="d-flex gap-1">
    <!-- TODO: check unsuporrted prop: offset-y -->
    <v-menu
      v-model="showDatePicker"
      :close-on-content-click="false"
      :offset="40"
      transition="scale-transition"
      min-width="auto"
      :style="{ 'z-index': '99999' }"
    >
      <template v-slot:activator="{ props }">
        <v-text-field
          :model-value="formattedDate"
          prepend-inner-icon="mdi-calendar"
          variant="outlined"
          readonly
          density="compact"
          hide-details
          data-testid="content-creator-schedule-date"
          v-bind="props"
        />
      </template>
      <v-date-picker
        v-model="selectedDate"
        :min="startOfDay(new Date())"
        @update:model-value="dateChanged"
      />
    </v-menu>
    <!-- TODO: check unsuporrted prop : offset-y -->
    <v-menu
      ref="menu"
      v-model="showTimePicker"
      :close-on-content-click="false"
      :offset="40"
      transition="scale-transition"
      max-width="290px"
      min-width="290px"
      :style="{ 'z-index': '99999' }"
    >
      <template v-slot:activator="{ props }">
        <v-text-field
          :model-value="formattedTime"
          prepend-inner-icon="mdi-clock-time-four-outline"
          readonly
          variant="outlined"
          density="compact"
          hide-details
          data-testid="content-creator-schedule-time"
          v-bind="props"
        />
      </template>
      <v-time-picker
        v-if="showTimePicker"
        v-model="selectedTime"
        full-width
        :format="timeFormat"
        :min="minTime"
        @update:model-value="timeChanged"
      />
    </v-menu>
  </div>
</template>

<script>
import { DATE_LONG, formatDate, getTime, timePickerFormat } from '@/lib/dateTime'

export default {
  props: {
    value: {
      type: String,
      default: () => new Date().toISOString(),
    },
  },

  data() {
    return {
      showDatePicker: false,
      showTimePicker: false,
      selectedDate: this.getDateFromValue(),
      selectedTime: '00:00',
    }
  },
  computed: {
    minTime() {
      if (!this.isToday) return null
      return new Date().toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },
    isToday() {
      const today = this.startOfDay(new Date())
      const selected = this.startOfDay(new Date(this.selectedDate))
      return today.getTime() === selected.getTime()
    },
    formattedDate() {
      return formatDate(this.value, DATE_LONG) || formatDate(new Date(), DATE_LONG)
    },
    formattedTime() {
      return getTime(this.value) || getTime(new Date())
    },
    // selectedTime stays 24h 'HH:mm'; only the picker UI follows the locale (e.g. AM/PM for en-US)
    timeFormat() {
      return timePickerFormat()
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        try {
          const date = new Date(newValue)
          this.selectedDate = date
          this.selectedTime = this.toTime24(date)
        } catch {
          const now = new Date()
          this.selectedDate = now
          this.selectedTime = this.toTime24(now)
        }
      },
    },
  },
  methods: {
    toTime24(date) {
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    },
    startOfDay(date) {
      const newDate = new Date(date)
      newDate.setHours(0, 0, 0, 0)
      return newDate
    },
    getDateFromValue() {
      try {
        return new Date(this.value)
      } catch {
        return new Date()
      }
    },
    emitUpdate(date) {
      // :min only disables clock values (and is cached); the AM/PM toggle, picking the hour
      // before the minute, or switching the date to today can still yield a past time. Move it
      // to the next whole minute instead of restoring the old value, which would reset the
      // hour the user just picked.
      const now = new Date()
      if (date <= now) {
        date.setTime(now.getTime())
        date.setSeconds(0, 0)
        date.setMinutes(date.getMinutes() + 1)

        // Set it after the picker received the raw value; if the corrected time equals the
        // previous model, Vuetify would otherwise see no change and keep showing the raw one.
        const corrected = this.toTime24(date)
        this.$nextTick(() => {
          this.selectedTime = corrected
        })
      }

      this.$emit('input', date.toISOString())
    },
    dateChanged(newDate) {
      if (!newDate) return

      const currentDate = new Date(this.value)
      const [hours, minutes] = this.selectedTime.split(':').map(Number)

      currentDate.setFullYear(newDate.getFullYear())
      currentDate.setMonth(newDate.getMonth())
      currentDate.setDate(newDate.getDate())
      currentDate.setHours(hours)
      currentDate.setMinutes(minutes)

      this.selectedDate = newDate
      this.emitUpdate(currentDate)
      this.showDatePicker = false
    },
    // update:hour carries the 1-12 clock value in ampm mode, so use the 24h model value instead
    timeChanged(time) {
      if (!time) return

      const currentDate = new Date(this.value)
      const [hours, minutes] = time.split(':').map(Number)

      currentDate.setHours(hours, minutes, 0, 0)

      this.emitUpdate(currentDate)
    },
  },
  emits: ['input'],
}
</script>

<style scoped>
:deep(.v-card > *:first-child:not(.v-btn):not(.v-chip):not(.v-avatar)),
:deep(.v-card > *:last-child:not(.v-btn):not(.v-chip):not(.v-avatar)) {
  border-radius: 0;
}
:deep(.v-time-picker-controls__time) button {
  height: unset !important;
}
</style>
